import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";

type ToolInputSchema = {
  type: "object";
  properties?: Record<string, unknown>;
  required?: string[];
};

type ToolDefinition = {
  name: string;
  description: string;
  inputSchema: ToolInputSchema;
  execute: (input: Record<string, unknown>) => unknown;
};

type ModelContext = {
  registerTool: (
    tool: ToolDefinition,
    options?: { signal?: AbortSignal },
  ) => void;
};

declare global {
  type NavigatorWithModelContext = Navigator & {
    modelContext?: ModelContext;
  };
}

export function WebMCPRegistrar() {
  const navigate = useNavigate();

  useEffect(() => {
    const modelContext = (navigator as NavigatorWithModelContext).modelContext;
    if (!modelContext) return;

    const controller = new AbortController();

    modelContext.registerTool(
      {
        name: "navigate",
        description:
          "Navigate to a path on wonda.sh (e.g. '/docs', '/blog', '/pricing', '/feature/<slug>').",
        inputSchema: {
          type: "object",
          properties: {
            path: {
              type: "string",
              description: "Absolute path starting with '/'.",
            },
          },
          required: ["path"],
        },
        execute: async (input) => {
          const path = typeof input.path === "string" ? input.path : "/";
          await navigate({ to: path });
          return { navigatedTo: path };
        },
      },
      { signal: controller.signal },
    );

    modelContext.registerTool(
      {
        name: "search_docs",
        description:
          "Open the Wonda CLI documentation. Optionally jump to a specific doc slug.",
        inputSchema: {
          type: "object",
          properties: {
            slug: {
              type: "string",
              description:
                "Optional doc slug (e.g. 'getting-started'). Omit to open the docs index.",
            },
          },
        },
        execute: async (input) => {
          const slug = typeof input.slug === "string" ? input.slug : null;
          const target = slug ? `/docs/${slug}` : "/docs";
          await navigate({ to: target });
          return { openedDoc: target };
        },
      },
      { signal: controller.signal },
    );

    modelContext.registerTool(
      {
        name: "install_cli",
        description:
          "Get the install command for the Wonda CLI (curl, brew, or npm).",
        inputSchema: {
          type: "object",
          properties: {
            channel: {
              type: "string",
              enum: ["curl", "brew", "npm"],
              description: "Preferred install channel.",
            },
          },
        },
        execute: (input) => {
          const channel =
            typeof input.channel === "string" ? input.channel : "curl";
          const commands: Record<string, string> = {
            curl: "curl -fsSL https://wonda.sh/install.sh | bash",
            brew: "brew tap degausai/tap && brew install wonda",
            npm: "npm install -g @degausai/wonda",
          };
          return { command: commands[channel] ?? commands.curl };
        },
      },
      { signal: controller.signal },
    );

    return () => controller.abort();
  }, [navigate]);

  return null;
}
